import React from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { Weapon } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

function formatDamageChip(weapon: Weapon): string | null {
  if (weapon.damageModifier == null) return null;
  return `${weapon.damageModifier >= 0 ? '+' : ''}${weapon.damageModifier}`;
}

interface SelectWeaponModalProps {
  open: boolean;
  memberName: string;
  excludeWeaponIds: string[];
  onClose: () => void;
  onSelect: (weapon: Weapon) => void;
}

export const SelectWeaponModal: React.FC<SelectWeaponModalProps> = ({
  open,
  memberName,
  excludeWeaponIds,
  onClose,
  onSelect,
}) => {
  const allWeapons = useReferenceDataStore((s) => s.weapons);
  const availableWeapons = React.useMemo(
    () =>
      [...allWeapons]
        .filter((w) => !excludeWeaponIds.includes(w.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allWeapons, excludeWeaponIds]
  );

  const [selected, setSelected] = React.useState<Weapon | null>(null);

  React.useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected);
      setSelected(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Weapon — {memberName}</DialogTitle>
      <DialogContent dividers>
        {availableWeapons.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No weapons available to add.
          </Typography>
        ) : (
          <List disablePadding>
            {availableWeapons.map((weapon) => {
              const dmgLabel = formatDamageChip(weapon);
              return (
                <ListItemButton
                  key={weapon.id}
                  selected={selected?.id === weapon.id}
                  onClick={() => setSelected(weapon)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        fontWeight={selected?.id === weapon.id ? 600 : 400}
                      >
                        {weapon.name}
                      </Typography>
                    }
                    secondary={
                      <>
                        {dmgLabel && (
                          <Chip
                            label={`Damage ${dmgLabel}`}
                            size="small"
                            sx={{ mr: 1, mb: 0.5 }}
                          />
                        )}
                        {weapon.description && (
                          <Typography variant="caption" color="text.secondary" component="span">
                            {weapon.description}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!selected} onClick={handleConfirm}>
          Add Weapon
        </Button>
      </DialogActions>
    </Dialog>
  );
};
